import { LanguagesEnum } from '@/api/enums';
import { ListingRequest } from '@/api/types';
import { Account } from '@/client/accounts';
import { Branch } from '@/client/branches';
import { EmailTemplate } from '@/client/email-templates';
import { File } from '@/client/files';
import { Group } from '@/client/groups';
import { PageTemplate } from '@/client/page-templates';
import { SendingProfile } from '@/client/sender-profiles';
import { User } from '@/client/users';
import { TargetEntities } from '@/common/types';
import { Benchmark } from './benchmarks.type';
import {
  CampaignEmailingType,
  CampaignPhishingSignEnum,
} from './campaigns.type';

export type CampaignSummary = {
  total: number;
  emailsSent: number;
  emailsOpen: number;
  clickedLinks: number;
  submittedData: number;
  emailsReported: number;
  errors: number;
  hasFalsePositives: boolean;
};

export type CampaignHistoryEventMeta = {
  payload: any;
  browser: any;
};

export type CampaignHistoryEvent = {
  id: string;
  time: Date;
  status: CampaignUserResultMessagesType;
  meta: CampaignHistoryEventMeta;
  isFalsePositive: boolean;
};

export enum CampaignStatusesEnum {
  ACTIVE = 'active',
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  COMPLETED = 'completed',
  PENDING_LAUNCH = 'pending-launch',
  PENDING_COMPLETION = 'pending-completion',
  FAILED_LAUNCH = 'failed-launch',
  FAILED_COMPLETION = 'failed-completion',
  PLACEHOLDER = 'placeholder',
}

export const CampaignStatusMessages = {
  [CampaignStatusesEnum.ACTIVE]: 'campaign.status.active',
  [CampaignStatusesEnum.DRAFT]: 'campaign.status.draft',
  [CampaignStatusesEnum.SCHEDULED]: 'campaign.status.scheduled',
  [CampaignStatusesEnum.COMPLETED]: 'campaign.status.completed',
  [CampaignStatusesEnum.PENDING_LAUNCH]: 'campaign.status.launch',
  [CampaignStatusesEnum.PENDING_COMPLETION]: 'campaign.status.complete',
  [CampaignStatusesEnum.FAILED_LAUNCH]: 'campaign.status.failLaunch',
  [CampaignStatusesEnum.FAILED_COMPLETION]: 'campaign.status.failComplete',
  [CampaignStatusesEnum.PLACEHOLDER]: 'phishing.autoSchedule.placeholder.label',
};

export enum CampaignUserResultStatusesEnum {
  CAMPAIGN_CREATED = 'campaign-created',
  EMAIL_SENT = 'email-sent',
  EMAIL_OPENED = 'email-opened',
  CLICKED_LINK = 'clicked-link',
  SUBMITTED_DATA = 'submitted-data',
  EMAIL_REPORTED = 'email-reported',
  ERROR = 'error',
  ERROR_SENDING_EMAIL = 'error-sending-email',
  SCHEDULED = 'scheduled',
  SENDING = 'sending',
}

export enum CampaignEventStatusesEnum {
  CAMPAIGN_CREATED = 'campaign-created',
  EMAIL_SENT = 'email-sent',
  EMAIL_OPENED = 'email-opened',
  CLICKED_LINK = 'clicked-link',
  SUBMITTED_DATA = 'submitted-data',
  EMAIL_REPORTED = 'email-reported',
  ERROR_SENDING_EMAIL = 'error-sending-email',
}

export const CampaignEventStatusMessages: Record<
  CampaignEventStatusesEnum,
  string
> = {
  [CampaignEventStatusesEnum.CAMPAIGN_CREATED]: 'campaign.event.status.created',
  [CampaignEventStatusesEnum.EMAIL_SENT]: 'campaign.event.status.emailSent',
  [CampaignEventStatusesEnum.EMAIL_OPENED]: 'campaign.event.status.emailOpened',
  [CampaignEventStatusesEnum.CLICKED_LINK]: 'campaign.event.status.clickedLink',
  [CampaignEventStatusesEnum.SUBMITTED_DATA]: 'campaign.event.status.submitted',
  [CampaignEventStatusesEnum.EMAIL_REPORTED]:
    'campaign.event.status.emailReported',
  [CampaignEventStatusesEnum.ERROR_SENDING_EMAIL]:
    'campaign.event.status.errorSending',
};

export const CampaignUserResultMessages = {
  [CampaignUserResultStatusesEnum.CAMPAIGN_CREATED]:
    'campaign.event.status.created',
  [CampaignUserResultStatusesEnum.EMAIL_SENT]:
    'campaign.event.status.emailSent',
  [CampaignUserResultStatusesEnum.EMAIL_OPENED]:
    'campaign.event.status.emailOpened',
  [CampaignUserResultStatusesEnum.CLICKED_LINK]:
    'campaign.event.status.clickedLink',
  [CampaignUserResultStatusesEnum.SUBMITTED_DATA]:
    'campaign.event.status.submitted',
  [CampaignUserResultStatusesEnum.EMAIL_REPORTED]:
    'campaign.event.status.emailReported',
  [CampaignUserResultStatusesEnum.ERROR]: 'campaign.event.status.errorSending',
  [CampaignUserResultStatusesEnum.ERROR_SENDING_EMAIL]:
    'campaign.event.status.errorSending',
  [CampaignUserResultStatusesEnum.SCHEDULED]: 'campaign.event.status.scheduled',
  [CampaignUserResultStatusesEnum.SENDING]: 'campaign.event.status.sending',
};

export type CampaignUserResultMessagesType =
  keyof typeof CampaignUserResultMessages;

export type CampaignUserResultMeta = {
  id: string;
  position: string;
  ip: string;
  latitude: number;
  longitude: number;
  sendDate: Date;
  modifiedDate: Date;
  reported: boolean;
};

export type CampaignUserResult = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  status: CampaignUserResultStatusesEnum;
  events: CampaignHistoryEvent[];
  user: User;
  meta: CampaignUserResultMeta;
  lastEventStatus?: CampaignUserResultStatusesEnum;
  falsePositive?: boolean;
};

export type CampaignMeta = {
  encodeEmailTemplateImages: boolean;
  goPhishId: string;
  goPhishGroupId: string;
  jobId: string;
  origin: string;
  cloneOf: string;
  cloneOfDifficulty: number;
  cloneOfName: string;
  phishingSign?: CampaignPhishingSignEnum;
};

export type CampaignBenchmark = {
  clickedLinksPercentage: number;
  submittedDataPercentage: number;
  emailsReportedPercentage: number;
};

export enum CampaignType {
  CAMPAIGN = 'campaign',
  TEMPLATE = 'template',
  WHITELISTING_TEST = 'whitelisting-test',
}

export type Campaign = {
  id: string;
  name: string;
  created: Date;
  updated: Date;
  launchDate: Date;
  sendByDate: Date;
  completedDate: Date;
  emailTemplate: EmailTemplate;
  landingPage: PageTemplate;
  sendingProfile: SendingProfile;
  status: CampaignStatusesEnum;
  url: string;
  results: CampaignUserResult[];
  timeline: CampaignHistoryEvent[];
  account: Account;
  createdBy: User;
  useDynamicTarget: boolean | null;
  dynamicTargetSetAt: Date | null;
  users: User[];
  targets: TargetEntities;
  excludes: TargetEntities;
  branches: Branch[];
  groups: Group[];
  meta: CampaignMeta;
  type: CampaignType;
  summary: CampaignSummary;
  language?: LanguagesEnum;
  masterTemplateName?: string;
  senderProfileType?: SenderProfileType;
  clickedLinksPercentage?: number;
  submittedDataPercentage?: number;
  description?: string;
  benchmarkSummary?: CampaignBenchmark;
  benchmark?: Benchmark;
  isAutoPhishing?: boolean;
  isWhitelisted?: boolean;
  completeAfter?: Date;
  isIndividual?: boolean | null;
  periodType?: CampaignEmailingType;
  languageGroupId?: string | null;
  languageFilterMode?: 'include' | 'fallback' | null;
  languageTargetingMethod?: 'single' | 'auto' | null;
  targetUsersCount?: number;
  thumbnail?: File;
  masterTemplate?: {
    id: string;
    active?: boolean;
    difficulty?: number | null;
    thumbnail?: File | null;
    translations?: Array<{
      language: string;
      name: string | null;
      description: string | null;
    }>;
    phishingSign?: {
      id: string;
      key: string;
      translations?: Array<{
        language: string;
        content: string | null;
      }>;
    } | null;
    characteristics?: Array<{
      id: string;
      translations?: Array<{
        language: string;
        description: string | null;
      }>;
    }>;
  } | null;
};

export type CampaignTemplate = {
  isPublished: boolean;
  difficulty?: number;
  isAiGenerated?: boolean;
} & Campaign;

export enum SenderProfileType {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}

export type CreateCampaignFromTemplateFormState = {
  selectedCampaignTemplate?: Campaign;
};

export type GetCampaignRequest = Pick<ListingRequest, 'filters'>;
